import * as React from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

import { SUPPLIER_CATEGORY_OPTIONS } from "@/api/suppliers.constraints";
import { useCreateSupplierMutation } from "@/api/suppliers";
import { useGetActiveDirectoryUsersQuery } from "@/api/users-admin";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { SupplierCreateRequest } from "@/types/supplier";

import {
  createSupplierDefaultValues,
  optional,
  parseCreateSupplierError,
  type CreateSupplierFormValues,
} from "./create-supplier-form";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateSupplierDialog = ({ open, onOpenChange }: Props) => {
  const navigate = useNavigate();
  const { data: owners = [], isLoading: ownersLoading } = useGetActiveDirectoryUsersQuery();
  const [createSupplier, { isLoading }] = useCreateSupplierMutation();
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<CreateSupplierFormValues>({ defaultValues: createSupplierDefaultValues });

  React.useEffect(() => {
    if (!open) reset(createSupplierDefaultValues);
  }, [open, reset]);

  const setOpen = (nextOpen: boolean) => {
    if (!isLoading) onOpenChange(nextOpen);
  };

  const onSubmit = async (values: CreateSupplierFormValues) => {
    const request: SupplierCreateRequest = {
      name: values.name.trim(),
      approved_supplier: values.approved_supplier,
      preferred_supplier: values.preferred_supplier,
      critical_supplier: values.critical_supplier,
      external_id: optional(values.external_id),
      owner_user_id: values.owner_user_id || null,
      contact_name: optional(values.contact_name),
      contact_email: optional(values.contact_email),
      contact_phone: optional(values.contact_phone),
      contact_notes: optional(values.contact_notes),
    };

    try {
      const supplier = await createSupplier(request).unwrap();
      toast.success("Supplier created");
      onOpenChange(false);
      navigate(`/suppliers/${supplier.id}`);
    } catch (error) {
      const parsed = parseCreateSupplierError(error);
      setError(parsed.target, { message: parsed.message });
    }
  };

  const inputError = (name: keyof CreateSupplierFormValues) => errors[name]?.message;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent closeDisabled={isLoading} className="max-h-[90vh] sm:max-w-xl">
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <DialogHeader>
            <DialogTitle>Create Supplier</DialogTitle>
            <DialogDescription>
              Add the legal entity and its default contact. Contract Families can be assigned after creation.
            </DialogDescription>
          </DialogHeader>

          <fieldset disabled={isLoading} className="mt-5 space-y-3 border-b pb-4">
            <legend className="text-sm font-semibold">Supplier categories</legend>
            {SUPPLIER_CATEGORY_OPTIONS.map(([field, label]) => (
              <div key={field} className="flex items-center gap-2">
                <input id={`create-${field}`} type="checkbox" className="size-4 accent-primary" {...register(field)} />
                <Label htmlFor={`create-${field}`}>{label}</Label>
              </div>
            ))}
          </fieldset>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="supplier-name">Supplier name</Label>
              <Input
                id="supplier-name"
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? "supplier-name-error" : undefined}
                maxLength={255}
                autoFocus
                {...register("name", { validate: value => Boolean(value.trim()) || "Supplier name is required" })}
              />
              {inputError("name") ? (
                <p id="supplier-name-error" role="alert" className="text-xs text-red-600">
                  {inputError("name")}
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="supplier-external-id">Supplier ID</Label>
              <Input id="supplier-external-id" maxLength={255} placeholder="Optional" {...register("external_id")} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="supplier-owner">Owner</Label>
              <select
                id="supplier-owner"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                aria-invalid={Boolean(errors.owner_user_id)}
                aria-describedby={errors.owner_user_id ? "supplier-owner-error" : undefined}
                disabled={ownersLoading}
                {...register("owner_user_id")}
              >
                <option value="">Unassigned</option>
                {owners.map(owner => (
                  <option key={owner.id} value={owner.id}>
                    {[owner.name, owner.surname].filter(Boolean).join(" ") || owner.email}
                  </option>
                ))}
              </select>
              {inputError("owner_user_id") ? (
                <p id="supplier-owner-error" role="alert" className="text-xs text-red-600">
                  {inputError("owner_user_id")}
                </p>
              ) : null}
            </div>

            <div className="border-t pt-4 sm:col-span-2">
              <p className="text-sm font-semibold text-slate-900">Default contact</p>
              <p className="mt-1 text-xs text-slate-500">Optional defaults inherited by assigned Contract Families.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="supplier-contact-name">Contact name</Label>
              <Input id="supplier-contact-name" maxLength={255} {...register("contact_name")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="supplier-contact-email">Contact email</Label>
              <Input id="supplier-contact-email" type="email" maxLength={320} {...register("contact_email")} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="supplier-contact-phone">Contact phone</Label>
              <Input id="supplier-contact-phone" maxLength={64} {...register("contact_phone")} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="supplier-contact-notes">Notes</Label>
              <Textarea id="supplier-contact-notes" maxLength={4000} rows={3} {...register("contact_notes")} />
            </div>
          </div>

          {errors.root?.server ? (
            <p role="alert" className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {errors.root.server.message}
            </p>
          ) : null}

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || ownersLoading}>
              {isLoading ? "Creating…" : "Create Supplier"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
